import { useCallback, useEffect, useMemo, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { useGLTF, useAnimations } from "@react-three/drei";
import * as THREE from "three";
import { SkeletonUtils } from "three-stdlib";
import { Interactive } from "../src/interactions/Interactive";
import { useInteractionStore } from "../src/stores/useInteraction";

const STANDBY_INDEX = 2;
const PROXIMITY_INDEX = 1;
const AUTO_INDEX = 4;
const PROXIMITY_RADIUS = 10;
const ROTATION_DURATION = 0.2;

export default function BananaCat({
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = 1,
  targetRef = null,
  ...props
}) {
  const group = useRef();
  const { scene, animations } = useGLTF("/interactive/bananna_cat.glb");
  const clonedScene = useMemo(() => SkeletonUtils.clone(scene), [scene]);
  const { actions } = useAnimations(animations, group);
  const modelBounds = useMemo(() => new THREE.Box3(), []);
  const modelCenter = useMemo(() => new THREE.Vector3(), []);

  const actionNames = useMemo(() => Object.keys(actions || {}), [actions]);
  const getActionName = (index) =>
    actionNames.length > 0 ? actionNames[Math.min(index, actionNames.length - 1)] : undefined;
  const standbyName = getActionName(STANDBY_INDEX);
  const proximityName = getActionName(PROXIMITY_INDEX);
  const autoName = getActionName(AUTO_INDEX);

  const baseEuler = useMemo(
    () => new THREE.Euler(rotation[0], rotation[1], rotation[2]),
    [rotation]
  );
  const catWorldPos = useMemo(() => new THREE.Vector3(), []);
  const targetWorldPos = useMemo(() => new THREE.Vector3(), []);
  const lookDirection = useMemo(() => new THREE.Vector3(), []);

  const isPlayingRef = useRef(false);
  const proximityTriggeredRef = useRef(false);
  const autoTimeoutRef = useRef(null);
  const playStandbyRef = useRef(null);
  const triggerProximityRef = useRef(null);
  const triggerAutoRef = useRef(null);
  const scheduleAutoRef = useRef(() => {});
  const proximityActionRef = useRef(null);
  const autoActionRef = useRef(null);
  const currentModeRef = useRef("standby");
  const autoEnterTriggeredRef = useRef(false);
  const cameraWorldPos = useMemo(() => new THREE.Vector3(), []);

  const camera = useThree((state) => state.camera);
  const playerHidden = useInteractionStore((state) => state.playerHidden);

  const lerpAngle = useCallback((current, target, alpha) => {
    let diff = ((target - current + Math.PI) % (Math.PI * 2)) - Math.PI;
    if (diff < -Math.PI) diff += Math.PI * 2;
    return current + diff * alpha;
  }, []);

  const interactive = useMemo(
    () =>
      new Interactive({
        id: "banana-cat",
        promptText: "Interactuar [E]",
        hudText: "¡Hola soy Aguacate! necesito de tu ayuda para encontrar a mis peluches, son muy especiales para mi. A cambio, por tu ayuda te abriré la puerta de salida que se encuentra abajo, ahí guardo algo muy especial para tí.  \nPD. ¡Feliz cumpleaños! 🎉🎂 ",
  radius: PROXIMITY_RADIUS,
        cameraConfig: {
          position: [6.49, 12.75, -67.91],
          transitionDuration: 1.2,
        },
        getCameraTarget: () => {
          if (!group.current) return null;
          modelBounds.setFromObject(group.current);
          modelBounds.getCenter(modelCenter);
          return [modelCenter.x, modelCenter.y, modelCenter.z];
        },
      }),
    []
  );

  useEffect(() => () => interactive.dispose(), [interactive]);

  useEffect(() => {
    clonedScene.traverse((child) => {
      if (child.isMesh || child.isSkinnedMesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });
  }, [clonedScene]);

  useEffect(() => {
    if (group.current) {
      group.current.rotation.set(baseEuler.x, baseEuler.y, baseEuler.z);
    }
  }, [baseEuler]);

  useEffect(() => {
    if (!actions || !standbyName) return undefined;

    const standbyAction = actions[standbyName];
    if (!standbyAction) return undefined;

    const proximityAction = proximityName ? actions?.[proximityName] : undefined;
    const autoAction = autoName ? actions?.[autoName] : undefined;
    const allActions = Object.values(actions);

    const clearAutoTimeout = () => {
      if (autoTimeoutRef.current !== null) {
        clearTimeout(autoTimeoutRef.current);
        autoTimeoutRef.current = null;
      }
    };

    const scheduleAutoAction = () => {
      if (!autoAction) return;
      if (autoTimeoutRef.current !== null) return;

      autoTimeoutRef.current = window.setTimeout(() => {
        autoTimeoutRef.current = null;

        if (!autoActionRef.current) {
          scheduleAutoAction();
          return;
        }

        if (isPlayingRef.current) {
          scheduleAutoAction();
          return;
        }

        if (currentModeRef.current !== "standby") {
          scheduleAutoAction();
          return;
        }

        triggerAutoRef.current && triggerAutoRef.current();
      }, 6000);
    };

    const playStandby = () => {
      clearAutoTimeout();
      allActions.forEach((clip) => {
        if (clip !== standbyAction) clip.stop();
      });

      standbyAction.reset().setLoop(THREE.LoopRepeat, Infinity).fadeIn(0.1).play();
      standbyAction.clampWhenFinished = false;
      isPlayingRef.current = false;
      currentModeRef.current = "standby";

      if (autoAction) {
        scheduleAutoAction();
      }
    };

    const triggerProximity = () => {
      if (!proximityAction) return;
      if (isPlayingRef.current) return;

      isPlayingRef.current = true;
      currentModeRef.current = "proximity";
      clearAutoTimeout();
      standbyAction.fadeOut(0.1);
      proximityAction.reset().setLoop(THREE.LoopOnce, 1).play();
      proximityAction.clampWhenFinished = true;
    };

    const triggerAuto = () => {
      if (!autoAction) return;
      if (isPlayingRef.current) return;

      isPlayingRef.current = true;
      currentModeRef.current = "auto";
      clearAutoTimeout();
      standbyAction.fadeOut(0.1);
      autoAction.reset().setLoop(THREE.LoopOnce, 1).play();
      autoAction.clampWhenFinished = true;
    };

    scheduleAutoRef.current = scheduleAutoAction;

    playStandbyRef.current = playStandby;
    triggerProximityRef.current = proximityAction ? triggerProximity : null;
    triggerAutoRef.current = autoAction ? triggerAuto : null;
    proximityActionRef.current = proximityAction ?? null;
    autoActionRef.current = autoAction ?? null;

    playStandby();

    const mixer =
      proximityAction?._mixer || autoAction?._mixer || standbyAction?._mixer || null;

    const handleFinished = (event) => {
      if (!event || !event.action) return;
      if (event.action === proximityAction || event.action === autoAction) {
        playStandby();
      }
    };

    if (mixer) {
      mixer.addEventListener("finished", handleFinished);
    }

    return () => {
      clearAutoTimeout();
      if (mixer) {
        mixer.removeEventListener("finished", handleFinished);
      }
      standbyAction.stop();
      proximityAction?.stop();
      autoAction?.stop();
      isPlayingRef.current = false;
      proximityActionRef.current = null;
      autoActionRef.current = null;
      currentModeRef.current = "standby";
      scheduleAutoRef.current = () => {};
    };
  }, [actions, standbyName, proximityName, autoName]);

  useFrame((_, delta) => {
    const catGroup = group.current;
    if (!catGroup) return;

    catGroup.rotation.x = baseEuler.x;
    catGroup.rotation.z = baseEuler.z;

    const targetGroup = targetRef?.current?.group;
    if (!targetGroup || typeof targetGroup.translation !== "function") {
      interactive.setWithinRadius(false);
      const alpha = Math.min(1, delta / ROTATION_DURATION);
      catGroup.rotation.y = lerpAngle(catGroup.rotation.y, baseEuler.y, alpha);
      return;
    }

    const translation = targetGroup.translation();
    if (!translation) {
      interactive.setWithinRadius(false);
      const alpha = Math.min(1, delta / ROTATION_DURATION);
      catGroup.rotation.y = lerpAngle(catGroup.rotation.y, baseEuler.y, alpha);
      return;
    }

    targetWorldPos.set(translation.x, translation.y, translation.z);
    catGroup.getWorldPosition(catWorldPos);

    const distance = catWorldPos.distanceTo(targetWorldPos);
  interactive.setWithinRadius(distance <= interactive.radius, distance);

    let desiredYaw = baseEuler.y;

    if (distance > PROXIMITY_RADIUS) {
      if (currentModeRef.current !== "standby") {
        playStandbyRef.current && playStandbyRef.current();
      }
      proximityTriggeredRef.current = false;
      const alpha = Math.min(1, delta / ROTATION_DURATION);
      catGroup.rotation.y = lerpAngle(catGroup.rotation.y, desiredYaw, alpha);
      return;
    }

    lookDirection.subVectors(targetWorldPos, catWorldPos);
    lookDirection.y = 0;

    if (playerHidden) {
      camera.getWorldPosition(cameraWorldPos);
      lookDirection.subVectors(cameraWorldPos, catWorldPos);
      lookDirection.y = 0;
      if (lookDirection.lengthSq() > 1e-5) {
        lookDirection.normalize();
        desiredYaw = Math.atan2(lookDirection.x, lookDirection.z);
      }
    } else if (lookDirection.lengthSq() > 1e-5) {
      lookDirection.normalize();
      desiredYaw = Math.atan2(lookDirection.x, lookDirection.z);
    }

    if (!autoEnterTriggeredRef.current) {
      const entered = interactive.enterInteraction();
      if (entered) {
        autoEnterTriggeredRef.current = true;
      }
    }

    if (!proximityTriggeredRef.current && !isPlayingRef.current) {
      proximityTriggeredRef.current = true;
      triggerProximityRef.current && triggerProximityRef.current();
    }

    if (
      currentModeRef.current === "auto" &&
      autoActionRef.current &&
      typeof autoActionRef.current.isRunning === "function" &&
      !autoActionRef.current.isRunning()
    ) {
      playStandbyRef.current && playStandbyRef.current();
    }

    if (
      currentModeRef.current === "standby" &&
      autoActionRef.current &&
      autoTimeoutRef.current === null
    ) {
      scheduleAutoRef.current && scheduleAutoRef.current();
    }

    const alpha = Math.min(1, delta / ROTATION_DURATION);
    catGroup.rotation.y = lerpAngle(catGroup.rotation.y, desiredYaw, alpha);
  });

  return (
    <primitive
      ref={group}
      object={clonedScene}
      position={position}
      scale={scale}
      {...props}
    />
  );
}

useGLTF.preload("/interactive/bananna_cat.glb");
